/**
 * Structured FAQs for all practice areas
 * Optimized for AI/LLM readability and SEO
 */

export interface FAQ {
  question: string;
  answer: string;
}

export interface PracticeAreaFAQs {
  en: {
    title: string;
    summary: string[];
    faqs: FAQ[];
  };
  es: {
    title: string;
    summary: string[];
    faqs: FAQ[];
  };
  vi: {
    title: string;
    summary: string[];
    faqs: FAQ[];
  };
}

export const immigrationFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Immigration Law Services",
    summary: [
      "Daniel Albert Law Firm provides comprehensive immigration legal services in Houston, Texas",
      "Services include asylum, citizenship, family-based immigration, work visas, and deportation defense",
      "Trilingual support in English, Spanish, and Vietnamese",
      "Free initial consultations available for all immigration matters",
      "Experienced representation for complex cases including waivers and appeals"
    ],
    faqs: [
      {
        question: "How long does the immigration process take?",
        answer: "Processing times vary significantly depending on the type of case and USCIS workload. Family-based green cards can take 6-24 months, while asylum cases may take 1-3 years. Employment-based cases vary from 3-18 months."
      },
      {
        question: "Do I need a lawyer for my immigration case?",
        answer: "While not required by law, having an experienced immigration attorney significantly increases your chances of success. Immigration law is complex and constantly changing."
      },
      {
        question: "What if I'm facing deportation?",
        answer: "If you've received a Notice to Appear or are in removal proceedings, contact us immediately. Time is critical in deportation cases. We can explore relief options including asylum, cancellation of removal, and appeals."
      },
      {
        question: "Can I work while my immigration case is pending?",
        answer: "It depends on your case type and current status. Some applicants can obtain work permits (EAD) while their green card or asylum application is pending."
      },
      {
        question: "What's the difference between a visa and a green card?",
        answer: "A visa is temporary permission to enter/stay in the U.S. for a specific purpose. A green card (permanent residence) allows you to live and work permanently in the U.S. and eventually apply for citizenship."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Servicios de Inmigración",
    summary: [
      "Daniel Albert Law Firm proporciona servicios legales de inmigración integrales en Houston, Texas",
      "Servicios incluyen asilo, ciudadanía, inmigración familiar, visas de trabajo y defensa contra deportación",
      "Soporte trilingüe en inglés, español y vietnamita",
      "Consultas iniciales gratuitas disponibles para todos los asuntos de inmigración",
      "Representación experimentada para casos complejos incluyendo perdones y apelaciones"
    ],
    faqs: [
      {
        question: "¿Cuánto tiempo toma el proceso de inmigración?",
        answer: "Los tiempos varían según el tipo de caso. Las tarjetas verdes familiares toman 6-24 meses, los casos de asilo 1-3 años, y los basados en empleo 3-18 meses."
      },
      {
        question: "¿Necesito un abogado para mi caso de inmigración?",
        answer: "Aunque no es requerido por ley, tener un abogado experimentado aumenta significativamente sus posibilidades de éxito. La ley de inmigración es compleja."
      },
      {
        question: "¿Qué pasa si enfrento deportación?",
        answer: "Si recibió un Aviso de Comparecencia, contáctenos inmediatamente. Podemos explorar opciones de alivio incluyendo asilo, cancelación de remoción y apelaciones."
      },
      {
        question: "¿Puedo trabajar mientras mi caso está pendiente?",
        answer: "Depende del tipo de caso. Algunos solicitantes pueden obtener permisos de trabajo (EAD) mientras su solicitud está pendiente."
      },
      {
        question: "¿Cuál es la diferencia entre visa y tarjeta verde?",
        answer: "Una visa es permiso temporal. Una tarjeta verde permite vivir y trabajar permanentemente en EE.UU. y solicitar ciudadanía."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Dịch Vụ Di Trú",
    summary: [
      "Daniel Albert Law Firm cung cấp dịch vụ pháp lý di trú toàn diện tại Houston, Texas",
      "Dịch vụ bao gồm tị nạn, quốc tịch, di trú gia đình, visa làm việc và bào chữa trục xuất",
      "Hỗ trợ ba ngôn ngữ: tiếng Anh, tiếng Tây Ban Nha và tiếng Việt",
      "Tư vấn ban đầu miễn phí cho tất cả các vấn đề di trú",
      "Đại diện dày dạn kinh nghiệm cho các vụ việc phức tạp"
    ],
    faqs: [
      {
        question: "Quy trình di trú mất bao lâu?",
        answer: "Thời gian xử lý khác nhau tùy loại trường hợp. Thẻ xanh gia đình mất 6-24 tháng, tị nạn 1-3 năm, việc làm 3-18 tháng."
      },
      {
        question: "Tôi có cần luật sư không?",
        answer: "Tuy không bắt buộc, có luật sư di trú giàu kinh nghiệm tăng đáng kể cơ hội thành công. Luật di trú rất phức tạp."
      },
      {
        question: "Nếu tôi đối mặt trục xuất thì sao?",
        answer: "Nếu nhận được thông báo trình diện, hãy liên hệ ngay. Chúng tôi có thể tìm các lựa chọn cứu trợ bao gồm tị nạn và kháng cáo."
      },
      {
        question: "Tôi có thể làm việc khi hồ sơ đang xử lý không?",
        answer: "Tùy thuộc vào loại trường hợp. Một số người nộp đơn có thể xin giấy phép làm việc (EAD)."
      },
      {
        question: "Visa và thẻ xanh khác nhau thế nào?",
        answer: "Visa là quyền tạm thời. Thẻ xanh cho phép sống và làm việc vĩnh viễn tại Mỹ và xin quốc tịch."
      }
    ]
  }
};

export const criminalDefenseFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Criminal Defense",
    summary: [
      "Aggressive criminal defense representation in Houston, Texas for all charges",
      "Services include DWI/DUI, drug charges, assault, family violence, sex crimes, and theft",
      "Protect your constitutional rights: remain silent, have an attorney, no illegal searches",
      "Available for urgent matters and arrests during business hours",
      "Experience with trial litigation, negotiations, and case dismissals"
    ],
    faqs: [
      {
        question: "Should I talk to the police without a lawyer?",
        answer: "No. You have the constitutional right to remain silent and to have an attorney present during questioning. Don't make any statements without your lawyer."
      },
      {
        question: "What happens at my first court appearance?",
        answer: "Your first appearance is typically the arraignment where charges are read and you enter a plea. Do not plead guilty without consulting an attorney first."
      },
      {
        question: "Can my criminal charges be dismissed?",
        answer: "Possibly. Charges can be dismissed for many reasons including insufficient evidence, illegal searches, witness issues, or procedural errors."
      },
      {
        question: "What's the difference between a misdemeanor and a felony?",
        answer: "Misdemeanors are less serious with penalties up to 1 year in county jail. Felonies are more serious with penalties of 2 years to life in state prison."
      },
      {
        question: "Will I go to jail?",
        answer: "Not necessarily. Many factors affect sentencing including the charge, your history, and defense strategy. We work to minimize or avoid jail time."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Defensa Penal",
    summary: [
      "Representación agresiva en defensa penal en Houston, Texas para todos los cargos",
      "Servicios incluyen DWI/DUI, drogas, agresión, violencia familiar, delitos sexuales y robo",
      "Proteja sus derechos constitucionales: guardar silencio, tener abogado, no registros ilegales",
      "Disponible para asuntos urgentes y arrestos durante horario de oficina",
      "Experiencia en litigio, negociaciones y desestimación de casos"
    ],
    faqs: [
      {
        question: "¿Debo hablar con la policía sin un abogado?",
        answer: "No. Tiene derecho constitucional a guardar silencio y tener un abogado presente durante el interrogatorio."
      },
      {
        question: "¿Qué pasa en mi primera audiencia?",
        answer: "La primera audiencia es típicamente la instrucción de cargos. No se declare culpable sin consultar primero con un abogado."
      },
      {
        question: "¿Pueden desestimarse mis cargos?",
        answer: "Posiblemente. Los cargos pueden desestimarse por evidencia insuficiente, registros ilegales, problemas con testigos o errores procesales."
      },
      {
        question: "¿Diferencia entre delito menor y grave?",
        answer: "Delitos menores tienen penas hasta 1 año en cárcel del condado. Los graves tienen penas de 2 años a cadena perpetua."
      },
      {
        question: "¿Iré a la cárcel?",
        answer: "No necesariamente. Muchos factores afectan la sentencia. Trabajamos para minimizar o evitar tiempo en prisión."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Bào Chữa Hình Sự",
    summary: [
      "Đại diện bào chữa hình sự mạnh mẽ tại Houston, Texas cho mọi cáo buộc",
      "Dịch vụ bao gồm DWI/DUI, ma túy, hành hung, bạo lực gia đình, tội phạm tình dục và trộm cắp",
      "Bảo vệ quyền hiến pháp: im lặng, có luật sư, không khám xét trái phép",
      "Sẵn sàng cho các vấn đề khẩn cấp và bắt giữ trong giờ làm việc",
      "Kinh nghiệm tranh tụng, đàm phán và bác bỏ cáo buộc"
    ],
    faqs: [
      {
        question: "Có nên nói chuyện với cảnh sát khi không có luật sư?",
        answer: "Không. Bạn có quyền hiến định được im lặng và có luật sư khi thẩm vấn."
      },
      {
        question: "Điều gì xảy ra tại phiên tòa đầu tiên?",
        answer: "Phiên đầu tiên thường là buộc tội. Đừng nhận tội mà không tham vấn luật sư trước."
      },
      {
        question: "Cáo buộc có thể bị bác bỏ không?",
        answer: "Có thể. Cáo buộc có thể bị bác bỏ vì chứng cứ không đủ, khám xét trái phép, vấn đề nhân chứng."
      },
      {
        question: "Khác nhau giữa tội nhẹ và trọng tội?",
        answer: "Tội nhẹ phạt đến 1 năm tù quận. Trọng tội phạt từ 2 năm đến chung thân."
      },
      {
        question: "Tôi có vào tù không?",
        answer: "Chưa chắc. Nhiều yếu tố ảnh hưởng mức án. Chúng tôi nỗ lực giảm thiểu hoặc tránh tù."
      }
    ]
  }
};

export const familyLawFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Family Law Services",
    summary: [
      "Compassionate family law representation in Houston, Texas",
      "Services include divorce, child custody, child support, CPS defense, and adoption",
      "Texas is a community property state - assets acquired during marriage are divided equally",
      "60-day waiting period required for all divorces in Texas",
      "Child's best interests are always the priority in custody matters"
    ],
    faqs: [
      {
        question: "How long does a divorce take in Texas?",
        answer: "Texas requires a minimum 60-day waiting period from filing. Uncontested divorces can be finalized shortly after. Contested divorces may take 6-12 months or longer."
      },
      {
        question: "How is property divided in a Texas divorce?",
        answer: "Texas is a community property state. Property acquired during marriage is generally divided equally, but the court considers many factors for a 'just and right' division."
      },
      {
        question: "How is child custody determined?",
        answer: "Texas courts prioritize the child's best interests. Factors include each parent's ability to care for the child, the child's emotional needs, and maintaining stability."
      },
      {
        question: "What should I do if CPS contacts me?",
        answer: "Contact an attorney immediately. You have rights during CPS investigations. Don't make statements or sign documents without legal counsel present."
      },
      {
        question: "Can I modify a custody or support order?",
        answer: "Yes, if there's been a material and substantial change in circumstances. This includes job changes, relocation, or changes in the child's needs."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Derecho Familiar",
    summary: [
      "Representación compasiva en derecho familiar en Houston, Texas",
      "Servicios incluyen divorcio, custodia, manutención, defensa CPS y adopción",
      "Texas es estado de bienes gananciales - activos del matrimonio se dividen igualmente",
      "Período de espera de 60 días requerido para todos los divorcios en Texas",
      "El mejor interés del menor es siempre la prioridad en custodia"
    ],
    faqs: [
      {
        question: "¿Cuánto tiempo toma un divorcio en Texas?",
        answer: "Texas requiere mínimo 60 días de espera. Divorcios no contenciosos se finalizan poco después. Contenciosos pueden tomar 6-12 meses o más."
      },
      {
        question: "¿Cómo se dividen los bienes en Texas?",
        answer: "Texas es estado de bienes gananciales. Bienes adquiridos durante el matrimonio generalmente se dividen igualmente."
      },
      {
        question: "¿Cómo se determina la custodia?",
        answer: "Los tribunales priorizan el mejor interés del menor. Factores incluyen capacidad de cada padre, necesidades emocionales y estabilidad."
      },
      {
        question: "¿Qué debo hacer si CPS me contacta?",
        answer: "Contacte a un abogado inmediatamente. Tiene derechos durante investigaciones de CPS. No haga declaraciones sin asesoría legal."
      },
      {
        question: "¿Puedo modificar una orden de custodia?",
        answer: "Sí, si hay un cambio material y sustancial en las circunstancias. Esto incluye cambios de trabajo, reubicación o cambios en necesidades del menor."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Luật Gia Đình",
    summary: [
      "Đại diện tận tâm về luật gia đình tại Houston, Texas",
      "Dịch vụ bao gồm ly hôn, quyền nuôi con, cấp dưỡng, bào chữa CPS và nhận con nuôi",
      "Texas là bang tài sản chung - tài sản hôn nhân chia đều",
      "Thời gian chờ 60 ngày bắt buộc cho mọi vụ ly hôn tại Texas",
      "Lợi ích tốt nhất của trẻ luôn là ưu tiên trong quyền nuôi"
    ],
    faqs: [
      {
        question: "Ly hôn ở Texas mất bao lâu?",
        answer: "Texas yêu cầu tối thiểu 60 ngày chờ. Ly hôn thuận tình hoàn tất sau đó. Tranh chấp có thể mất 6-12 tháng hoặc lâu hơn."
      },
      {
        question: "Tài sản chia thế nào ở Texas?",
        answer: "Texas là bang tài sản chung. Tài sản trong hôn nhân thường chia đều."
      },
      {
        question: "Quyền nuôi con được quyết định thế nào?",
        answer: "Tòa ưu tiên lợi ích tốt nhất của trẻ. Yếu tố bao gồm khả năng chăm sóc, nhu cầu tình cảm và sự ổn định."
      },
      {
        question: "Tôi nên làm gì nếu CPS liên hệ?",
        answer: "Liên hệ luật sư ngay. Bạn có quyền trong điều tra CPS. Đừng khai hay ký gì mà không có luật sư."
      },
      {
        question: "Có thể sửa đổi lệnh nuôi con không?",
        answer: "Có, nếu có thay đổi hoàn cảnh đáng kể. Bao gồm thay đổi việc làm, di chuyển hoặc thay đổi nhu cầu của trẻ."
      }
    ]
  }
};

export const businessLawFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Business Law Services",
    summary: [
      "Comprehensive business law services in Houston, Texas for startups to enterprises",
      "Services include LLC/Corporation formation, contracts, trademark registration, and M&A",
      "Registered agent services available for Texas businesses",
      "Debt collection and insurance claims representation",
      "Strategic guidance for business growth and protection"
    ],
    faqs: [
      {
        question: "What type of business entity should I form?",
        answer: "It depends on your goals. LLCs offer flexibility and liability protection. Corporations are better for investors and going public. We'll help you choose the best structure."
      },
      {
        question: "How long does it take to form a business in Texas?",
        answer: "Filing with the Secretary of State typically takes 2-5 business days. With expedited processing, it can be done in 24 hours. Complete setup including EIN takes about 1-2 weeks."
      },
      {
        question: "Do I need a trademark for my business name?",
        answer: "A trademark protects your brand beyond just your state. If you plan to expand or operate online, federal trademark registration is highly recommended."
      },
      {
        question: "What should be in an operating agreement?",
        answer: "Key provisions include ownership percentages, voting rights, profit distribution, management structure, and exit strategies for members."
      },
      {
        question: "How can you help with debt collection?",
        answer: "We send demand letters, file lawsuits if needed, and pursue judgment enforcement including bank levies, wage garnishments, and property liens."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Derecho Empresarial",
    summary: [
      "Servicios integrales de derecho empresarial en Houston, Texas",
      "Servicios incluyen formación de LLC/Corporación, contratos, marcas registradas y fusiones",
      "Servicios de agente registrado disponibles para empresas de Texas",
      "Cobranza de deudas y representación en reclamos de seguros",
      "Orientación estratégica para crecimiento y protección empresarial"
    ],
    faqs: [
      {
        question: "¿Qué tipo de entidad debo formar?",
        answer: "Depende de sus objetivos. LLC ofrece flexibilidad y protección. Corporaciones son mejores para inversionistas. Le ayudamos a elegir."
      },
      {
        question: "¿Cuánto tiempo toma formar un negocio en Texas?",
        answer: "El registro ante el Secretario de Estado toma 2-5 días hábiles. Con procesamiento expedito, 24 horas. Configuración completa toma 1-2 semanas."
      },
      {
        question: "¿Necesito registrar mi marca?",
        answer: "Una marca protege su negocio más allá del estado. Si planea expandirse u operar en línea, el registro federal es muy recomendable."
      },
      {
        question: "¿Qué debe incluir un acuerdo operativo?",
        answer: "Disposiciones clave incluyen porcentajes de propiedad, derechos de voto, distribución de ganancias y estrategias de salida."
      },
      {
        question: "¿Cómo ayudan con cobranza de deudas?",
        answer: "Enviamos cartas de demanda, presentamos demandas y perseguimos ejecución de sentencias incluyendo embargos bancarios y gravámenes."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Luật Doanh Nghiệp",
    summary: [
      "Dịch vụ luật doanh nghiệp toàn diện tại Houston, Texas",
      "Dịch vụ bao gồm thành lập LLC/Công ty, hợp đồng, đăng ký thương hiệu và M&A",
      "Dịch vụ đại lý đăng ký có sẵn cho doanh nghiệp Texas",
      "Thu hồi nợ và đại diện bồi thường bảo hiểm",
      "Hướng dẫn chiến lược cho tăng trưởng và bảo vệ doanh nghiệp"
    ],
    faqs: [
      {
        question: "Tôi nên thành lập loại hình doanh nghiệp nào?",
        answer: "Tùy thuộc vào mục tiêu. LLC linh hoạt và bảo vệ trách nhiệm. Công ty tốt cho nhà đầu tư. Chúng tôi giúp bạn chọn."
      },
      {
        question: "Thành lập doanh nghiệp ở Texas mất bao lâu?",
        answer: "Đăng ký với Bộ trưởng Ngoại giao mất 2-5 ngày làm việc. Xử lý nhanh 24 giờ. Hoàn chỉnh mất 1-2 tuần."
      },
      {
        question: "Tôi có cần đăng ký thương hiệu không?",
        answer: "Thương hiệu bảo vệ doanh nghiệp vượt ra ngoài bang. Nếu mở rộng hoặc hoạt động trực tuyến, đăng ký liên bang rất được khuyến khích."
      },
      {
        question: "Thỏa thuận điều hành nên có gì?",
        answer: "Các điều khoản chính bao gồm tỷ lệ sở hữu, quyền biểu quyết, phân phối lợi nhuận và chiến lược thoát."
      },
      {
        question: "Các bạn giúp thu hồi nợ thế nào?",
        answer: "Chúng tôi gửi thư yêu cầu, khởi kiện và theo đuổi thi hành án bao gồm phong tỏa ngân hàng và xiết nợ."
      }
    ]
  }
};

export const personalInjuryFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Personal Injury Claims",
    summary: [
      "No win, no fee - we advance all case costs for personal injury claims",
      "Texas has a 2-year statute of limitations for most personal injury cases",
      "You can recover medical expenses, lost wages, pain and suffering, and property damage",
      "Texas follows 'modified comparative negligence' - you can recover if less than 51% at fault",
      "Never accept insurance company's first offer without consulting an attorney"
    ],
    faqs: [
      {
        question: "How much is my personal injury case worth?",
        answer: "Case value depends on severity of injuries, medical expenses, lost wages, permanent disability, and pain and suffering. We provide free case evaluations."
      },
      {
        question: "How long do I have to file a personal injury claim in Texas?",
        answer: "Texas has a 2-year statute of limitations for most personal injury cases from the date of injury. Some cases have shorter deadlines."
      },
      {
        question: "What if I was partially at fault for the accident?",
        answer: "Texas follows 'modified comparative negligence' - you can still recover if you're less than 51% at fault. Your compensation is reduced by your fault percentage."
      },
      {
        question: "Should I accept the insurance company's settlement offer?",
        answer: "No - not without consulting an attorney first. Insurance companies routinely make lowball initial offers. Once you accept, you can't get more money later."
      },
      {
        question: "How much does a personal injury lawyer cost?",
        answer: "We work on contingency - no fee unless we win your case. We advance all case expenses and only get paid when you receive compensation."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Reclamaciones por Lesiones",
    summary: [
      "Sin cobro si no ganamos - adelantamos todos los costos del caso",
      "Texas tiene un plazo de 2 años para la mayoría de los casos de lesiones",
      "Puede recuperar gastos médicos, salarios perdidos, dolor y sufrimiento, y daños a propiedad",
      "Texas aplica 'negligencia comparativa' - puede recuperar si su culpa es menor al 51%",
      "Nunca acepte la primera oferta de la aseguradora sin consultar un abogado"
    ],
    faqs: [
      {
        question: "¿Cuánto vale mi caso de lesiones personales?",
        answer: "El valor depende de la gravedad de lesiones, gastos médicos, salarios perdidos y dolor y sufrimiento. Ofrecemos evaluaciones gratuitas."
      },
      {
        question: "¿Cuánto tiempo tengo para presentar un reclamo?",
        answer: "Texas tiene un plazo de 2 años desde la fecha de la lesión. Algunos casos tienen plazos más cortos."
      },
      {
        question: "¿Y si tuve parte de la culpa?",
        answer: "Texas aplica 'negligencia comparativa modificada' - puede recuperar si su culpa es menor al 51%. La compensación se reduce según su porcentaje."
      },
      {
        question: "¿Debo aceptar la oferta de la aseguradora?",
        answer: "No - sin consultar primero con un abogado. Las aseguradoras ofrecen montos bajos inicialmente. Una vez que acepta, no puede obtener más."
      },
      {
        question: "¿Cuánto cuesta un abogado de lesiones?",
        answer: "Trabajamos por contingencia - no cobramos si no ganamos. Adelantamos gastos y solo cobramos cuando usted recibe compensación."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Yêu Cầu Bồi Thường Thương Tích",
    summary: [
      "Không thắng, không phí - chúng tôi ứng trước mọi chi phí vụ việc",
      "Texas có thời hiệu 2 năm cho hầu hết vụ việc thương tích",
      "Bạn có thể thu hồi chi phí y tế, mất thu nhập, đau đớn và thiệt hại tài sản",
      "Texas áp dụng 'sơ suất so sánh' - bạn có thể thu hồi nếu lỗi dưới 51%",
      "Đừng bao giờ chấp nhận đề nghị đầu tiên của bảo hiểm mà không tham vấn luật sư"
    ],
    faqs: [
      {
        question: "Vụ việc của tôi có giá trị bao nhiêu?",
        answer: "Giá trị phụ thuộc vào mức độ chấn thương, chi phí y tế, mất thu nhập và đau đớn. Chúng tôi đánh giá miễn phí."
      },
      {
        question: "Tôi có bao lâu để nộp yêu cầu ở Texas?",
        answer: "Texas có thời hiệu 2 năm kể từ ngày bị thương. Một số có thời hạn ngắn hơn."
      },
      {
        question: "Nếu tôi cũng có lỗi thì sao?",
        answer: "Texas áp dụng 'sơ suất so sánh điều chỉnh' - bạn vẫn thu hồi được nếu lỗi dưới 51%. Bồi thường giảm theo tỷ lệ lỗi."
      },
      {
        question: "Có nên chấp nhận đề nghị của bảo hiểm?",
        answer: "Không - hãy tham vấn luật sư trước. Bảo hiểm thường đề nghị thấp ban đầu. Đã chấp nhận thì không thể nhận thêm."
      },
      {
        question: "Thuê luật sư thương tích tốn bao nhiêu?",
        answer: "Chúng tôi làm việc theo tỷ lệ - không thu phí nếu không thắng. Ứng trước chi phí và chỉ nhận khi bạn được bồi thường."
      }
    ]
  }
};

export const realEstateFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Real Estate Law Services",
    summary: [
      "Full-service real estate law firm in Houston, Texas for residential and commercial transactions",
      "Services include title search, contract review, closings, and landlord-tenant matters",
      "Protection of property rights through thorough due diligence",
      "Foreclosure defense and eviction representation available",
      "Local expertise in Texas real estate law and Houston market"
    ],
    faqs: [
      {
        question: "Why do I need a real estate attorney?",
        answer: "Real estate transactions involve significant financial risk. An attorney reviews contracts, identifies issues, ensures clear title, and protects your interests throughout the process."
      },
      {
        question: "What is a title search and why is it important?",
        answer: "A title search examines public records to verify ownership and find any liens, easements, or encumbrances on the property that could affect your rights."
      },
      {
        question: "How long does a real estate closing take?",
        answer: "A typical closing takes 30-45 days from contract to closing. This includes inspections, title work, loan processing, and document preparation."
      },
      {
        question: "What should I look for in a lease agreement?",
        answer: "Key terms include rent amount, security deposit, maintenance responsibilities, termination clauses, and any restrictions on use or modifications."
      },
      {
        question: "What are my options if facing foreclosure?",
        answer: "Options include loan modification, short sale, deed in lieu, bankruptcy, or legal defense against improper foreclosure proceedings."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Derecho Inmobiliario",
    summary: [
      "Bufete de servicio completo en derecho inmobiliario en Houston, Texas",
      "Servicios incluyen búsqueda de título, revisión de contratos, cierres y asuntos de arrendamiento",
      "Protección de derechos de propiedad mediante diligencia debida",
      "Defensa contra ejecución hipotecaria y representación en desalojos",
      "Experiencia local en ley inmobiliaria de Texas y mercado de Houston"
    ],
    faqs: [
      {
        question: "¿Por qué necesito un abogado inmobiliario?",
        answer: "Las transacciones inmobiliarias implican riesgo financiero significativo. Un abogado revisa contratos, identifica problemas y protege sus intereses."
      },
      {
        question: "¿Qué es una búsqueda de título?",
        answer: "Una búsqueda de título examina registros públicos para verificar propiedad y encontrar gravámenes o servidumbres que puedan afectar sus derechos."
      },
      {
        question: "¿Cuánto tiempo toma un cierre inmobiliario?",
        answer: "Un cierre típico toma 30-45 días desde el contrato. Incluye inspecciones, trabajo de título, procesamiento de préstamo y preparación de documentos."
      },
      {
        question: "¿Qué debo buscar en un contrato de arrendamiento?",
        answer: "Términos clave incluyen monto de renta, depósito, responsabilidades de mantenimiento, cláusulas de terminación y restricciones."
      },
      {
        question: "¿Cuáles son mis opciones si enfrento ejecución hipotecaria?",
        answer: "Opciones incluyen modificación de préstamo, venta corta, escritura en lugar de ejecución, bancarrota o defensa legal."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Luật Bất Động Sản",
    summary: [
      "Văn phòng luật bất động sản dịch vụ đầy đủ tại Houston, Texas",
      "Dịch vụ bao gồm tìm kiếm sở hữu, xem xét hợp đồng, đóng cửa và vấn đề thuê nhà",
      "Bảo vệ quyền tài sản thông qua thẩm định kỹ lưỡng",
      "Bào chữa tịch thu và đại diện trục xuất có sẵn",
      "Chuyên môn địa phương về luật bất động sản Texas và thị trường Houston"
    ],
    faqs: [
      {
        question: "Tại sao tôi cần luật sư bất động sản?",
        answer: "Giao dịch bất động sản có rủi ro tài chính lớn. Luật sư xem xét hợp đồng, xác định vấn đề và bảo vệ lợi ích của bạn."
      },
      {
        question: "Tìm kiếm sở hữu là gì và tại sao quan trọng?",
        answer: "Tìm kiếm sở hữu kiểm tra hồ sơ công khai để xác minh quyền sở hữu và tìm khoản nợ hoặc ràng buộc có thể ảnh hưởng quyền của bạn."
      },
      {
        question: "Đóng cửa bất động sản mất bao lâu?",
        answer: "Đóng cửa thông thường mất 30-45 ngày từ hợp đồng. Bao gồm kiểm tra, tìm sở hữu, xử lý vay và chuẩn bị tài liệu."
      },
      {
        question: "Tôi nên xem gì trong hợp đồng thuê?",
        answer: "Các điều khoản chính bao gồm số tiền thuê, đặt cọc, trách nhiệm bảo trì, điều khoản chấm dứt và hạn chế."
      },
      {
        question: "Tôi có lựa chọn gì nếu đối mặt tịch thu?",
        answer: "Lựa chọn bao gồm sửa đổi vay, bán ngắn, chứng thư thay tịch thu, phá sản hoặc bào chữa pháp lý."
      }
    ]
  }
};

export const probateEstateFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Probate & Estate Planning",
    summary: [
      "Compassionate probate and estate planning services in Houston, Texas",
      "Services include wills, trusts, probate administration, and guardianship",
      "Proper planning helps avoid lengthy and costly probate proceedings",
      "Special needs trusts protect benefits for loved ones with disabilities",
      "We guide families through the entire probate process with care"
    ],
    faqs: [
      {
        question: "What is probate and when is it required?",
        answer: "Probate is the legal process of settling a deceased person's estate. It's required when the person owned assets in their name alone without beneficiary designations."
      },
      {
        question: "How long does the probate process take in Texas?",
        answer: "Simple estates may take 6-12 months. Complex estates with disputes or multiple assets can take 1-3 years or longer."
      },
      {
        question: "What's the difference between a will and a trust?",
        answer: "A will goes through probate and becomes public record. A trust avoids probate, provides privacy, and can offer more control over asset distribution."
      },
      {
        question: "Do I need an estate plan if I don't have many assets?",
        answer: "Yes. Estate planning isn't just about assets. It includes healthcare directives, powers of attorney, and naming guardians for minor children."
      },
      {
        question: "What is a special needs trust?",
        answer: "A special needs trust provides for a loved one with disabilities without disqualifying them from government benefits like SSI and Medicaid."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Sucesiones y Planificación Patrimonial",
    summary: [
      "Servicios compasivos de sucesiones y planificación patrimonial en Houston, Texas",
      "Servicios incluyen testamentos, fideicomisos, administración de sucesiones y tutela",
      "La planificación adecuada ayuda a evitar sucesiones largas y costosas",
      "Fideicomisos para necesidades especiales protegen beneficios de seres queridos con discapacidades",
      "Guiamos a las familias a través de todo el proceso de sucesión con cuidado"
    ],
    faqs: [
      {
        question: "¿Qué es la sucesión y cuándo se requiere?",
        answer: "La sucesión es el proceso legal de liquidar el patrimonio de un difunto. Se requiere cuando la persona poseía activos solo a su nombre."
      },
      {
        question: "¿Cuánto tiempo toma la sucesión en Texas?",
        answer: "Patrimonios simples toman 6-12 meses. Patrimonios complejos con disputas pueden tomar 1-3 años o más."
      },
      {
        question: "¿Diferencia entre testamento y fideicomiso?",
        answer: "El testamento pasa por sucesión y se hace público. El fideicomiso evita sucesión, proporciona privacidad y más control sobre distribución."
      },
      {
        question: "¿Necesito planificación patrimonial si no tengo muchos activos?",
        answer: "Sí. No es solo sobre activos. Incluye directivas de salud, poderes notariales y nombrar tutores para hijos menores."
      },
      {
        question: "¿Qué es un fideicomiso para necesidades especiales?",
        answer: "Provee para un ser querido con discapacidades sin descalificarlo de beneficios gubernamentales como SSI y Medicaid."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Thừa Kế & Lập Kế Hoạch Di Sản",
    summary: [
      "Dịch vụ thừa kế và lập kế hoạch di sản tận tâm tại Houston, Texas",
      "Dịch vụ bao gồm di chúc, quỹ tín thác, quản lý thừa kế và giám hộ",
      "Lập kế hoạch đúng cách giúp tránh thủ tục thừa kế dài và tốn kém",
      "Quỹ tín thác nhu cầu đặc biệt bảo vệ quyền lợi cho người thân khuyết tật",
      "Chúng tôi hướng dẫn gia đình qua toàn bộ quy trình thừa kế với sự quan tâm"
    ],
    faqs: [
      {
        question: "Thừa kế là gì và khi nào cần?",
        answer: "Thừa kế là quy trình pháp lý giải quyết di sản của người đã mất. Cần thiết khi người đó sở hữu tài sản chỉ dưới tên mình."
      },
      {
        question: "Quy trình thừa kế ở Texas mất bao lâu?",
        answer: "Di sản đơn giản mất 6-12 tháng. Di sản phức tạp có tranh chấp có thể mất 1-3 năm hoặc lâu hơn."
      },
      {
        question: "Khác nhau giữa di chúc và quỹ tín thác?",
        answer: "Di chúc qua thừa kế và thành hồ sơ công khai. Quỹ tín thác tránh thừa kế, bảo mật và kiểm soát phân phối tài sản hơn."
      },
      {
        question: "Tôi có cần lập kế hoạch di sản nếu không có nhiều tài sản?",
        answer: "Có. Không chỉ về tài sản. Bao gồm chỉ thị y tế, giấy ủy quyền và chỉ định người giám hộ cho con nhỏ."
      },
      {
        question: "Quỹ tín thác nhu cầu đặc biệt là gì?",
        answer: "Cung cấp cho người thân khuyết tật mà không làm mất quyền lợi chính phủ như SSI và Medicaid."
      }
    ]
  }
};

export const taxLawFAQs: PracticeAreaFAQs = {
  en: {
    title: "Quick Facts About Tax Law Services",
    summary: [
      "Comprehensive tax resolution, planning, and litigation services in Houston, Texas",
      "IRS audit representation and tax dispute resolution",
      "Tax debt negotiation including offers in compromise and installment agreements",
      "Strategic tax planning to minimize liability and maximize savings",
      "Direct access to your attorney throughout the process"
    ],
    faqs: [
      {
        question: "What should I do if I receive an IRS audit notice?",
        answer: "Contact a tax attorney immediately. Don't respond to the IRS without representation. We can represent you throughout the audit and protect your rights."
      },
      {
        question: "Can the IRS forgive tax debt?",
        answer: "Yes, through an Offer in Compromise. The IRS may accept less than the full amount owed if you can't pay in full and meet certain criteria."
      },
      {
        question: "What is an installment agreement?",
        answer: "An installment agreement allows you to pay tax debt over time in monthly payments. We negotiate favorable terms and stop collection actions."
      },
      {
        question: "How long can the IRS collect tax debt?",
        answer: "Generally, the IRS has 10 years to collect tax debt from the date of assessment. However, certain actions can extend this period."
      },
      {
        question: "What is innocent spouse relief?",
        answer: "Innocent spouse relief can protect you from tax liability caused by your spouse's errors or fraud on joint returns you filed."
      }
    ]
  },
  es: {
    title: "Datos Rápidos Sobre Servicios de Derecho Fiscal",
    summary: [
      "Servicios integrales de resolución fiscal, planificación y litigios en Houston, Texas",
      "Representación en auditorías del IRS y resolución de disputas fiscales",
      "Negociación de deudas fiscales incluyendo ofertas de compromiso y acuerdos de pago",
      "Planificación fiscal estratégica para minimizar responsabilidad y maximizar ahorros",
      "Acceso directo a su abogado durante todo el proceso"
    ],
    faqs: [
      {
        question: "¿Qué debo hacer si recibo notificación de auditoría del IRS?",
        answer: "Contacte a un abogado fiscal inmediatamente. No responda al IRS sin representación. Lo representamos durante la auditoría."
      },
      {
        question: "¿Puede el IRS perdonar deudas fiscales?",
        answer: "Sí, mediante una Oferta de Compromiso. El IRS puede aceptar menos del monto total si no puede pagar completamente."
      },
      {
        question: "¿Qué es un acuerdo de pagos a plazos?",
        answer: "Le permite pagar la deuda fiscal en pagos mensuales. Negociamos términos favorables y detenemos acciones de cobro."
      },
      {
        question: "¿Cuánto tiempo puede el IRS cobrar deudas fiscales?",
        answer: "Generalmente, el IRS tiene 10 años desde la fecha de evaluación. Ciertas acciones pueden extender este período."
      },
      {
        question: "¿Qué es el alivio de cónyuge inocente?",
        answer: "Puede protegerlo de responsabilidad fiscal causada por errores o fraude de su cónyuge en declaraciones conjuntas."
      }
    ]
  },
  vi: {
    title: "Thông Tin Nhanh Về Dịch Vụ Luật Thuế",
    summary: [
      "Dịch vụ giải quyết thuế, lập kế hoạch và tranh tụng toàn diện tại Houston, Texas",
      "Đại diện kiểm toán IRS và giải quyết tranh chấp thuế",
      "Đàm phán nợ thuế bao gồm đề nghị thỏa hiệp và thỏa thuận trả góp",
      "Lập kế hoạch thuế chiến lược để giảm thiểu trách nhiệm và tối đa hóa tiết kiệm",
      "Truy cập trực tiếp luật sư của bạn trong suốt quá trình"
    ],
    faqs: [
      {
        question: "Tôi nên làm gì khi nhận thông báo kiểm toán IRS?",
        answer: "Liên hệ luật sư thuế ngay. Đừng trả lời IRS mà không có đại diện. Chúng tôi đại diện bạn suốt cuộc kiểm toán."
      },
      {
        question: "IRS có thể xóa nợ thuế không?",
        answer: "Có, thông qua Đề nghị Thỏa hiệp. IRS có thể chấp nhận ít hơn số tiền nợ nếu bạn không thể trả đủ."
      },
      {
        question: "Thỏa thuận trả góp là gì?",
        answer: "Cho phép bạn trả nợ thuế theo thời gian bằng các khoản thanh toán hàng tháng. Chúng tôi đàm phán điều khoản thuận lợi."
      },
      {
        question: "IRS có thể thu nợ thuế trong bao lâu?",
        answer: "Thông thường, IRS có 10 năm từ ngày đánh giá. Một số hành động có thể kéo dài thời gian này."
      },
      {
        question: "Miễn trừ vợ/chồng vô tội là gì?",
        answer: "Có thể bảo vệ bạn khỏi trách nhiệm thuế do lỗi hoặc gian lận của vợ/chồng trong tờ khai chung."
      }
    ]
  }
};
